import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { Artisan } from '../types/entities';
import { RootState } from './store';
import { baseUrlApi } from '../../utils/url';

interface ArtisanState {
  artisans: Artisan[];
  artisan: Artisan | null;
  loading: boolean;
  error: string | null;
}

const initialState: ArtisanState = {
  artisans: [],
  artisan: null,
  loading: false,
  error: null,
};

export const fetchArtisans = createAsyncThunk<Artisan[], void, { state: RootState }>(
  'artisan/fetchArtisans',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.get(`${baseUrlApi}/artisans`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Échec du chargement des artisans');
    }
  }
);

// Nouvelle action pour récupérer les artisans par Job
export const fetchArtisansByJob = createAsyncThunk<Artisan[], number, { state: RootState }>(
  'artisan/fetchArtisansByJob',
  async (jobId, { getState, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.get(`${baseUrlApi}/artisans/by-job/${jobId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Échec du chargement des artisans par métier');
    }
  }
);

export const fetchArtisan = createAsyncThunk<Artisan, number, { state: RootState }>(
  'artisan/fetchArtisan',
  async (artisanId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.get(`${baseUrlApi}/artisans/${artisanId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchArtisans());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Échec du chargement de l\'artisan');
    }
  }
);

export const createArtisan = createAsyncThunk<Artisan, Partial<Artisan>, { state: RootState }>(
  'artisan/createArtisan',
  async (artisanData, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.post(`${baseUrlApi}/artisans`, artisanData, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchArtisans());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Échec de la création de l\'artisan');
    }
  }
);

export const updateArtisan = createAsyncThunk<
  Artisan,
  { artisanData: Partial<Artisan>; artisanId: number },
  { state: RootState }
>(
  'artisan/updateArtisan',
  async ({ artisanData, artisanId }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.put(`${baseUrlApi}/artisans/${artisanId}`, artisanData, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchArtisans());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Échec de la mise à jour de l\'artisan');
    }
  }
);

export const deleteArtisan = createAsyncThunk<number, number, { state: RootState }>(
  'artisan/deleteArtisan',
  async (artisanId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      await axios.delete(`${baseUrlApi}/artisans/${artisanId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchArtisans());
      return artisanId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Échec de la suppression de l\'artisan');
    }
  }
);

const artisanSlice = createSlice({
  name: 'artisan',
  initialState,
  reducers: {
    clearArtisan: (state) => { state.artisan = null; },
    clearArtisans: (state) => { state.artisans = []; },
    resetArtisanState: () => initialState,
  },
  extraReducers: (builder) => {
    [fetchArtisans, fetchArtisansByJob, fetchArtisan, createArtisan, updateArtisan, deleteArtisan].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload as string;
        });
    });

    builder
      .addCase(fetchArtisans.fulfilled, (state, action) => {
        state.loading = false;
        state.artisans = action.payload;
      })
      .addCase(fetchArtisansByJob.fulfilled, (state, action) => {
        state.loading = false;
        state.artisans = action.payload;
      })
      .addCase(fetchArtisan.fulfilled, (state, action) => {
        state.loading = false;
        state.artisan = action.payload;
      })
      .addCase(createArtisan.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateArtisan.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteArtisan.fulfilled, (state, action) => {
        state.loading = false;
        state.artisans = state.artisans.filter(item => item.id !== action.payload);
      });
  },
});

export const { clearArtisan, clearArtisans, resetArtisanState } = artisanSlice.actions;
export default artisanSlice.reducer;